
import Vue, { PropType } from 'vue';
import { CvButton } from '@carbon/vue';
import Close20 from '@carbon/icons-vue/es/close/20';
import ArrowLeft20 from '@carbon/icons-vue/es/arrow--left/20';
import { Artwork, VFileUploaderFile } from '@/components/w2pFileUploader';
import { ArtworkPayload } from '@/web-components/import-wizard/typings/pintura';

import {
  SmallDetailsPayload,
  DefaultSmallDetailsResult,
} from '@/typings/SmallDetails';
import {
  ImageProcessingEngineVersion,
  DoSmallDetailsPart,
} from '@/constants/imageProcessingEngine';
import { base64StringToBlob } from 'blob-util';
import { useStore as useGlobalStore } from '@/stores/v2/global';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { mapActions, mapState } from 'pinia';

import imageProcessingAPI from '@/services/FileService';
import { AnnotationShape } from '../../typings/common';

declare module 'vue/types/vue' {
  interface Vue {
    $sapCustomerId: string;
  }
}

export default Vue.extend({
  name: 'VectorizePreviewModal',
  inject: ['$sapCustomerId'],
  components: {
    CvButton,
    'close-icon': Close20,
    'arrow-left-icon': ArrowLeft20,
  },
  props: {
    vectorizedImageBlob: {
      type: Blob as PropType<Blob>,
      required: true,
    },
    vectorizedImageToken: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapState(useGlobalStore, ['isLoaderActive']),
    ...mapState(useShapeStore, ['activeShapeId', 'shapeById']),

    activeShape(): AnnotationShape | undefined {
      return this.shapeById(this.activeShapeId);
    },

    vectorizedImageUrl(): string {
      return URL.createObjectURL(this.vectorizedImageBlob);
    },
  },
  methods: {
    ...mapActions(useGlobalStore, ['setIsLoaderActive']),

    onReject() {
      this.$modal.hide('vectorize-preview-modal');
      this.$emit('reject');
    },

    async onAccept() {
      if (this.activeShape && this.activeShape.kind === 'Logo') {
        this.setIsLoaderActive(true);

        try {
          const { data } = await imageProcessingAPI.downloadVectorizedFile(
            this.vectorizedImageToken
          );

          const originalFilename = this.activeShape.title + '.pdf';
          const artworkPayload = await this.processVectorizedPdf(
            base64StringToBlob(data.vectorizedContentBytes, 'application/pdf'),
            originalFilename
          );

          this.setIsLoaderActive(false);

          if (artworkPayload) {
            this.$modal.hide('vectorize-preview-modal');
            this.$emit('vectorize-preview:accept', artworkPayload);
          }
        } catch (error) {
          console.error('Failed to process vectorized file:', error);
          this.setIsLoaderActive(false);
        }
      }
    },

    async processVectorizedPdf(
      pdfBlob: Blob,
      originalFilename: string
    ): Promise<ArtworkPayload | null> {
      if (!this.activeShape || this.activeShape.kind !== 'Logo') {
        return null;
      }

      const artwork: Artwork = {
        externalCustomerId: this.$sapCustomerId,
        customerId: this.activeShape.customerId,
        title: originalFilename,
        fileName: originalFilename,
        shouldSaveInLogoLibrary: true,
        shouldSaveDraftForWizard: true,
        currentEngineVersion: ImageProcessingEngineVersion,
      };

      const file: VFileUploaderFile = {
        file: new File([pdfBlob], originalFilename, {
          type: 'application/pdf',
        }),
        invalidMessage: '',
        invalidMessageTitle: '',
        state: '',
      };

      const { data: result } = await imageProcessingAPI.upload(artwork, file);

      const pngBlob = base64StringToBlob(result.pngFile, 'image/png');
      const traditionalTechniquePngBlob = result.traditionalTechniquePngFile
        ? base64StringToBlob(result.traditionalTechniquePngFile, 'image/png')
        : '';

      const smallDetailsResult: SmallDetailsPayload | undefined =
        DoSmallDetailsPart
          ? { ...DefaultSmallDetailsResult, isCheckValid: false }
          : undefined;

      return {
        ...result,
        id: result.personalizationArtworkId,
        pngFile: pngBlob,
        pngFileUrl: URL.createObjectURL(pngBlob),
        pngFileWithoutConverters: pngBlob,
        traditionalTechniquePngFile: traditionalTechniquePngBlob,
        customerId: this.activeShape.customerId,
        title: artwork.title || '',
        thumbnailUrl: '',
        processed: '',
        variantArtworkImages: [],
        smallDetailsResult,
        fileName: artwork.fileName,
      } as ArtworkPayload;
    },
  },
});
